import { useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { useLocation } from 'react-router-dom';
import { allowPerformAction, getActionElementClass } from '../../../../library/Core/SmartFunctions';

type OfferToStudent = {
    MENTORING_GUIDANCE: boolean;
    JOBS_INTERNSHIPS: boolean;
    SHORT_TERM_PROJECTS: boolean;
};

type OfferToInstitute = {
    ADVISORY_CONSULTING: boolean;
    CURRICULUM_DEV: boolean;
    PRODUCTS_SERVICES: boolean;
};

type ApiGetResponse = {
    success: boolean;
    exists: boolean;
    data: {
        student_id: string;
        institute_id: number;
        offer_to_student: OfferToStudent;
        offer_to_institute: OfferToInstitute;
    };
};

const ContributionPreferences = () => {
    const { state } = useContext(SmartContext);
    const model = state.internal.model;
    const [isExpanded, setIsExpanded] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [offerToStudent, setOfferToStudent] = useState<OfferToStudent>({
        MENTORING_GUIDANCE: false,
        JOBS_INTERNSHIPS: false,
        SHORT_TERM_PROJECTS: false,
    });

    const [offerToInstitute, setOfferToInstitute] = useState<OfferToInstitute>({
        ADVISORY_CONSULTING: false,
        CURRICULUM_DEV: false,
        PRODUCTS_SERVICES: false,
    });

    // institute details fetched from API
    const [instituteName, setInstituteName] = useState<string>('');
    const [instituteLogo, setInstituteLogo] = useState<string>('');

    const API_BASE_URL = `${process.env.REACT_APP_BROADCAST_SERVER}`;

    const location = useLocation();

    // -------- Student UUID from URL --------
    const getStudentIdFromUrl = () => {
        const pathParts = location.pathname.split('/');
        const alumniIndex = pathParts.indexOf('alumni');
        if (alumniIndex !== -1 && pathParts[alumniIndex + 1]) {
            return pathParts[alumniIndex + 1];
        }
        return null;
    };
    const student_id = getStudentIdFromUrl();

    // -------- Resolve institute_id for 3 login types --------
    const resolvedInstituteId = useMemo(() => {
        // 1) Student login: state.data.selectedInstitute.instituteId
        const institute_id_student = state?.data?.selectedInstitute?.instituteId;

        // 2) Super admin login: state.data.userInstitutes[1]?.instituteId (as per your current logic)
        // NOTE: safer is finding first valid instituteId from array; done below
        const institute_id_student_admin =
            state?.data?.userInstitutes?.find((x: any) => x?.instituteId)?.instituteId ||
            state?.data?.userInstitutes?.[1]?.instituteId;
console.log(state?.data?.userInstitutes,'test admin student id');
        // 3) Admin login: sessionStorage user.primaryInstitute
        const user = sessionStorage.getItem('user');
        const parsedUser = user ? JSON.parse(user) : null;
        const primaryInstitute = parsedUser?.primaryInstitute;

        // Priority order:
        // - Student selected institute
        // - Primary institute (admin)
        // - Any institute from userInstitutes (super admin)
        return institute_id_student || primaryInstitute || institute_id_student_admin || null;
    }, [state]);

    const institute_id = resolvedInstituteId;

    const capitalizeFirstLetter = (text: string): string => {
        if (!text) return text;
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    // -------- Fetch preferences --------
    const fetchData = async () => {
        if (!student_id || !institute_id) return;

        setLoading(true);
        try {
            const res = await axios.post<ApiGetResponse>(`${API_BASE_URL}/contribution/get-data`, {
                student_id,
                institute_id,
            });

            if (res.data?.success) {
                setOfferToStudent(res.data.data.offer_to_student);
                setOfferToInstitute(res.data.data.offer_to_institute);
            }
        } catch (e) {
            console.error('Error fetching contribution preferences', e);
        } finally {
            setLoading(false);
        }
    };

    // -------- Fetch institute name/logo --------
    const fetchInstituteMeta = async () => {
        if (!institute_id) return;

        try {
            const requestUrl = `${API_BASE_URL}/pre-signup/get-institute-name`;

            // your API expects array: { id: [numericId] }
            const resp = await axios.post(
                requestUrl,
                { id: [Number(institute_id)] },
                { timeout: 20000 }
            );

            const row = resp?.data?.result?.[0];
            if (resp?.data?.success && row) {
                if (row?.name) setInstituteName(String(row.name));
                if (row?.logo) setInstituteLogo(String(row.logo));
            }
        } catch (e) {
            console.error('Error fetching institute name/logo', e);
        }
    };

    const onToggleStudent = (key: keyof OfferToStudent) => {
        setOfferToStudent((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const onToggleInstitute = (key: keyof OfferToInstitute) => {
        setOfferToInstitute((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const onSave = async () => {
        if (saving) return;
        if (!student_id || !institute_id) {
            alert('Institute / Student not found. Please re-login.');
            return;
        }

        const confirmSave = window.confirm('Are you sure you want to update your contribution preferences?');
        if (!confirmSave) return;

        setSaving(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/contribution/update`, {
                student_id,
                institute_id,
                offer_to_student: offerToStudent,
                offer_to_institute: offerToInstitute,
            });

            if (res.data?.success) {
                alert('Preferences updated successfully.');
            } else {
                alert(res.data?.message || 'Unable to save preferences.');
            }
        } catch (e) {
            console.error('Error saving contribution preferences', e);
            alert('Something went wrong while saving.');
        } finally {
            setSaving(false);
        }
    };

    // -------- Effects --------
    // 1) When institute_id resolved, fetch institute name/logo
    useEffect(() => {
        if (institute_id) fetchInstituteMeta();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institute_id]);

    // 2) When student_id + institute_id present, fetch saved preferences
    useEffect(() => {
        if (student_id && institute_id) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [student_id, institute_id]);

    // prefer instituteName; fallback to school_settings name
    const schoolSettings = localStorage.getItem('school_settings');
    const schoolNameFromSettings = schoolSettings ? JSON.parse(schoolSettings).name : '';
    const finalInstituteName = instituteName || schoolNameFromSettings;

    return (
        <div className="lite-blue border-radius-6 py-4 px-4 my-4">
            <div className="row g-3">
                <div className="col-auto">
                    <img
                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-yellow-setting.svg"
                        width="31"
                        alt=""
                    />
                </div>
                <div className="col">
                    <div className="font-22 font-600">Contribution Preferences</div>

                  

                    {loading && <div className="font-12 opacity-75">Loading...</div>}
                </div>

                <div className="col-auto">
                    <button
                        className="bg-transparent border-0 icon-down-arrow fs-4"
                        type="button"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        aria-expanded={isExpanded}
                        aria-controls="collapseExample"
                    />
                </div>
            </div>

            <div className={`collapse ${isExpanded ? 'show' : ''}`} id="collapseExample">
                <hr className="my-4 border-color-0B34541A opacity-100" />

                <div className="pb-3">
                    <div className="row g-3">
                        {/* LEFT */}
                        <div className="col-sm-6">
                            <div className="row g-3">
                                <div className="col-auto">
                                    <img
                                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-blue-ribbon.svg"
                                        width="40"
                                        alt=""
                                    />
                                </div>

                                <div className="col">
                                    <div className="font-18 font-600">What can you offer to Current Students?</div>
                                    <p className="font-14 font-400 mb-4">Let your juniors know what you can help with.</p>

                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check1"
                                                    checked={offerToStudent.MENTORING_GUIDANCE}
                                                    onChange={model?.isEditable ? () => onToggleStudent('MENTORING_GUIDANCE') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check1">
                                                    Mentoring &amp; Guidance
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check2"
                                                    checked={offerToStudent.JOBS_INTERNSHIPS}
                                                    onChange={model?.isEditable ? () => onToggleStudent('JOBS_INTERNSHIPS') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check2">
                                                    Jobs and Internships
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check3"
                                                    checked={offerToStudent.SHORT_TERM_PROJECTS}
                                                    onChange={model?.isEditable ? () => onToggleStudent('SHORT_TERM_PROJECTS') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check3">
                                                    Short Term Projects
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT */}
                        <div className="col-sm-6">
                            <div className="row g-3">
                                <div className="col-auto">
                                    <img
                                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/icon-blue-ribbon.svg"
                                        width="40"
                                        alt=""
                                    />
                                </div>

                                <div className="col">
                                    <div className="font-18 font-600">
                                        What can you offer to {capitalizeFirstLetter(finalInstituteName)}?
                                    </div>
                                    <p className="font-14 font-400 mb-4">
                                        Let your alma mater know how can you contribute towards institute&apos;s growth.
                                    </p>

                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check4"
                                                    checked={offerToInstitute.ADVISORY_CONSULTING}
                                                    onChange={model?.isEditable ? () => onToggleInstitute('ADVISORY_CONSULTING') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check4">
                                                    Advisory &amp; Consulting
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check5"
                                                    checked={offerToInstitute.CURRICULUM_DEV}
                                                    onChange={model?.isEditable ? () => onToggleInstitute('CURRICULUM_DEV') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check5">
                                                    Curriculum Development Expertise
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="check6"
                                                    checked={offerToInstitute.PRODUCTS_SERVICES}
                                                    onChange={model?.isEditable ? () => onToggleInstitute('PRODUCTS_SERVICES') : undefined}
                                                    disabled={allowPerformAction() ? false : true}
                                                />
                                                <label className="form-check-label d-inline-block font-16 font-500" htmlFor="check6">
                                                    Products &amp; Services
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* optional: show logo if you want */}
                                    {/* {instituteLogo && <img src={instituteLogo} alt="Institute Logo" style={{ maxHeight: 40 }} />} */}
                                </div>
                            </div>
                        </div>

                        {/* BUTTON */}
                         {model.isEditable && (
                            <div className="col-12">
                                <button className={`btn-darkblue mx-auto height-45 mt-2 ${getActionElementClass()}`} onClick={onSave} disabled={saving || !allowPerformAction()}>
                                    <span>{saving ? 'Updating...' : 'Update Preferences'}</span>
                                </button>
                            </div>
                         )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContributionPreferences;
